import { Component, type ErrorInfo, type ReactNode } from "react";
import { logFieldCalendarCrash, logFieldCalendarUrlCrash } from "./fieldCalendarDiagnostics";
import { FIELD_CALENDAR_FALLBACK_HREF } from "./fieldCalendarUrlState";

type Props = {
  section: string;
  children: ReactNode;
};

type State = {
  hasError: boolean;
};

export class FieldCalendarErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logFieldCalendarCrash(this.props.section, error, {
      componentStack: info.componentStack,
    });
    logFieldCalendarUrlCrash("errorBoundary", error, {
      section: this.props.section,
    });
  }

  private resetCalendar = (): void => {
    this.setState({ hasError: false });
    try {
      window.location.assign(FIELD_CALENDAR_FALLBACK_HREF);
    } catch {
      /* ignore */
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const isRoot = this.props.section === "Field Calendar";
      return (
        <div
          className={
            isRoot
              ? "mx-4 my-8 rounded-lg border border-amber-300 bg-amber-50 px-6 py-10 text-center sm:mx-6"
              : "border border-amber-200 bg-amber-50 px-4 py-6 text-center"
          }
        >
          <p className="text-sm font-bold text-amber-900">
            {isRoot ? "Unable to restore calendar state" : "Unable to render calendar view"}
          </p>
          <p className="mt-2 text-xs text-amber-800">
            {isRoot
              ? "Saved calendar link or filters could not be loaded. You can open a fresh calendar view."
              : `${this.props.section} — refresh the page or switch view mode.`}
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-bold text-white"
              onClick={this.resetCalendar}
            >
              Open calendar
            </button>
            <button
              type="button"
              className="rounded-md border border-amber-400 px-3 py-1.5 text-xs font-bold text-amber-900"
              onClick={() => this.setState({ hasError: false })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
